import {
  ALLOWED_PDF_EXTENSIONS,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select, Steps } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

// Panel for "Consigner un visa entreprise generale envoye hors EtudesCarre":
// record a visa's general info + PDF, then attach remarks linked to the
// documents they concern.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VisaFormData = {
  contractId: number;
  dateEnvoi: string;
  numero: string;
};

type RemarkFormData = {
  description: string;
  documentVersionIds: number[];
};

type DocumentVersionOption = {
  id: number;
  label: string;
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

const STEP_ITEMS = [{ title: "Visa" }, { title: "Remarques" }];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ConsignerVisaPanelProps = {
  onClose: () => void;
};

export function ConsignerVisaPanel({ onClose }: ConsignerVisaPanelProps) {
  const { yard, contracts } = useYardContext();

  const [currentStep, setCurrentStep] = useState(0);
  const [visaId, setVisaId] = useState<number | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [documentVersions, setDocumentVersions] = useState<
    DocumentVersionOption[]
  >([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [remarksAdded, setRemarksAdded] = useState(0);

  const [isSubmittingVisa, setIsSubmittingVisa] = useState(false);
  const [isSubmittingRemark, setIsSubmittingRemark] = useState(false);

  const visaForm = useForm<VisaFormData>({
    defaultValues: {
      contractId: 0,
      dateEnvoi: new Date().toISOString().slice(0, 10),
      numero: "",
    },
  });

  const remarkForm = useForm<RemarkFormData>({
    defaultValues: { description: "", documentVersionIds: [] },
  });

  const selectedContract = visaForm.watch("contractId");
  const selectedVersionIds = remarkForm.watch("documentVersionIds");

  const contractOptions = contracts.map((c) => ({
    label: `${c.performerCompanyName} (${c.code ?? c.type})`,
    value: String(c.id),
  }));

  // -------------------------------------------------------------------------
  // Fetch the documents the remarks can target once the visa exists.
  // -------------------------------------------------------------------------

  const fetchDocumentVersions = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      const { data } = await apiClient.GET("/api/yards/{yardId}/documents", {
        params: { path: { yardId: String(yard.id) } },
      });
      if (isNil(data)) return;

      const options: DocumentVersionOption[] = [];
      for (const lot of data.data.lots) {
        for (const presta of lot.prestas) {
          for (const doc of presta.documents) {
            for (const version of doc.versions) {
              options.push({
                id: version.id,
                label: `${doc.number} - ${doc.name}${
                  version.index ? ` (${version.index})` : ""
                }`,
              });
            }
          }
        }
      }
      setDocumentVersions(options);
    } catch {
      // ignore - the remarks step simply shows an empty list
    } finally {
      setLoadingDocuments(false);
    }
  }, [yard.id]);

  useEffect(() => {
    if (isNotNil(visaId)) {
      fetchDocumentVersions();
    }
  }, [visaId, fetchDocumentVersions]);

  // -------------------------------------------------------------------------
  // Step 0: create the visa
  // -------------------------------------------------------------------------

  const handleVisaSubmit = visaForm.handleSubmit(async (formData) => {
    if (formData.contractId <= 0) {
      toaster.error({
        title: "Erreur",
        description: "Selectionnez un contrat",
      });
      return;
    }

    setIsSubmittingVisa(true);
    try {
      const metadata = {
        contractId: formData.contractId,
        dateEnvoi: new Date(formData.dateEnvoi).toISOString(),
        ...(formData.numero ? { numero: formData.numero } : {}),
      };

      const { data: result, error } = await apiClient.POST(
        "/api/yards/{yardId}/visas",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: pdfFile ?? undefined,
          },
          bodySerializer(body) {
            const fd = new FormData();
            for (const [key, value] of Object.entries(
              body as Record<string, unknown>,
            )) {
              if (value instanceof File) {
                fd.append(key, value, value.name);
              } else if (isNotNil(value)) {
                fd.append(key, String(value));
              }
            }
            return fd;
          },
        },
      );

      if (error || isNil(result)) {
        toaster.error({
          title: "Erreur",
          description: error?.error ?? "Echec de la creation du visa",
        });
        return;
      }

      setVisaId(result.data.visaId);
      setCurrentStep(1);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmittingVisa(false);
    }
  });

  // -------------------------------------------------------------------------
  // Step 1: add a remark linked to documents
  // -------------------------------------------------------------------------

  const handleRemarkSubmit = remarkForm.handleSubmit(async (formData) => {
    if (isNil(visaId)) return;

    if (formData.documentVersionIds.length === 0) {
      toaster.error({
        title: "Erreur",
        description: "Selectionnez au moins un document concerne",
      });
      return;
    }

    setIsSubmittingRemark(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/visas/{visaId}/comments",
        {
          params: {
            path: { yardId: String(yard.id), visaId: String(visaId) },
          },
          body: {
            description: formData.description,
            documentVersionIds: formData.documentVersionIds,
          },
        },
      );

      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Echec de l'ajout de la remarque",
        });
        return;
      }

      setRemarksAdded((prev) => prev + 1);
      toaster.success({ title: "Succes", description: "Remarque ajoutee" });
      remarkForm.reset();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmittingRemark(false);
    }
  });

  // -------------------------------------------------------------------------
  // File handler
  // -------------------------------------------------------------------------

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      isNil(ext) ||
      !ALLOWED_PDF_EXTENSIONS.includes(
        ext as (typeof ALLOWED_PDF_EXTENSIONS)[number],
      )
    ) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier doit etre au format PDF",
      });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier PDF depasse 50 Mo",
      });
      e.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  // -------------------------------------------------------------------------
  // Document version toggle
  // -------------------------------------------------------------------------

  const toggleVersion = (versionId: number) => {
    const current = selectedVersionIds;
    if (current.includes(versionId)) {
      remarkForm.setValue(
        "documentVersionIds",
        current.filter((id) => id !== versionId),
      );
    } else {
      remarkForm.setValue("documentVersionIds", [...current, versionId]);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <Steps.Root
        count={STEP_ITEMS.length}
        step={currentStep}
        onStepChange={(details) => setCurrentStep(details.step)}
        linear
      >
        <Steps.List>
          {STEP_ITEMS.map((item, index) => (
            <Steps.Item key={item.title} index={index}>
              <Steps.Trigger>
                <Steps.Indicator>{index + 1}</Steps.Indicator>
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  {item.title}
                </span>
              </Steps.Trigger>
              {index < STEP_ITEMS.length - 1 && <Steps.Separator />}
            </Steps.Item>
          ))}
        </Steps.List>

        {/* Step 0: Visa general info */}
        <Steps.Content index={0}>
          <form onSubmit={handleVisaSubmit} className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Informations du visa
            </h3>

            <div>
              <span className={labelClass}>
                Contrat <span className="text-red-500">*</span>
              </span>
              <Select
                options={contractOptions}
                value={selectedContract > 0 ? [String(selectedContract)] : []}
                onValueChange={(details) => {
                  const val = details.value[0];
                  visaForm.setValue("contractId", val ? Number(val) : 0);
                }}
                placeholder="Selectionnez un contrat"
              />
            </div>

            <div>
              <label htmlFor="visaDateEnvoi" className={labelClass}>
                Date d'envoi <span className="text-red-500">*</span>
              </label>
              <input
                id="visaDateEnvoi"
                type="date"
                {...visaForm.register("dateEnvoi")}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="visaNumero" className={labelClass}>
                N° du visa
              </label>
              <input
                id="visaNumero"
                type="text"
                {...visaForm.register("numero")}
                maxLength={255}
                className={inputClass}
                placeholder="Optionnel"
              />
            </div>

            <div>
              <label htmlFor="visaPdf" className={labelClass}>
                Fichier du visa (PDF)
              </label>
              <input
                id="visaPdf"
                type="file"
                accept=".pdf"
                onChange={handlePdfChange}
                className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
              />
              {isNotNil(pdfFile) && (
                <p className="mt-1 text-xs text-zinc-500">
                  {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} Mo)
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                outline
                onClick={onClose}
                disabled={isSubmittingVisa}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmittingVisa}>
                {isSubmittingVisa ? "En cours..." : "Suivant"}
              </Button>
            </div>
          </form>
        </Steps.Content>

        {/* Step 1: Remarks */}
        <Steps.Content index={1}>
          <form onSubmit={handleRemarkSubmit} className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Ajouter une remarque
            </h3>

            {remarksAdded > 0 && (
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
                {remarksAdded} remarque(s) ajoutee(s)
              </div>
            )}

            <div>
              <label htmlFor="remarkDescription" className={labelClass}>
                Remarque <span className="text-red-500">*</span>
              </label>
              <textarea
                id="remarkDescription"
                {...remarkForm.register("description")}
                required
                minLength={1}
                maxLength={5000}
                rows={4}
                className={inputClass}
                placeholder="Texte de la remarque"
              />
            </div>

            <div>
              <span className={labelClass}>
                Documents concernes <span className="text-red-500">*</span>
              </span>
              {loadingDocuments ? (
                <p className="text-sm text-zinc-500">Chargement...</p>
              ) : documentVersions.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  Aucun document accessible
                </p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-zinc-300 p-2 dark:border-zinc-700">
                  {documentVersions.map((version) => (
                    <label
                      key={version.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVersionIds.includes(version.id)}
                        onChange={() => toggleVersion(version.id)}
                      />
                      <span>{version.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                outline
                onClick={onClose}
                disabled={isSubmittingRemark}
              >
                Terminer
              </Button>
              <Button type="submit" disabled={isSubmittingRemark}>
                {isSubmittingRemark ? "En cours..." : "Ajouter et continuer"}
              </Button>
            </div>
          </form>
        </Steps.Content>
      </Steps.Root>
    </div>
  );
}
